import { useAuthStore } from '@/stores/auth';
import { useMessageStore, type Message } from '@/stores/messages';
import config from '@/config';
import { generateAuthToken } from '@/utils/auth';
import i18n from '@/i18n';
import {
  transformFormattedResponse,
  buildAdditionalInfo,
} from '@/utils/responseParser';
import { CLOUD_EVENT_TYPES } from '@/types';
import {
  MESSAGE_DIRECTION,
  PARTICIPANT_TYPE,
  PARTICIPANT_DISPLAY_NAME,
  PROCESSING_STAGE,
  CLOUD_EVENT,
  GENESYS_CLOUD,
  WS_MESSAGE_TYPE,
  MIDDLEWARE_PHASE,
} from '@/constants';
import type {
  GenesysCloudEvent,
  BackendCloudEvent,
  BackendResponse,
  SessionStatus,
  ConnectionEventData,
  ProcessingEventData,
  ErrorEventData,
  StatusUpdateEventData,
  TranslationRequestData,
  TranslationResponseData,
  TranslationStatusData,
  SummaryResponseData,
  SummaryStatusData,
  SummaryErrorData,
  SimilarityResponseData,
  SimilarityStatusData,
  SimilarityErrorData,
  StatusEventData,
  WorkflowRetryData,
  WorkflowFailedData,
} from '@/types';

/**
 * Resolves the configured middleware endpoint into a bare `host[:port]` plus a
 * flag for whether it should use the insecure ws/http schemes.
 *
 * Accepts both a bare host (`localhost:5000`) and a full URL
 * (`http://localhost:5000/path`): any scheme and path are stripped. When an
 * explicit scheme is present it decides secure vs insecure; otherwise a
 * localhost/loopback host is treated as insecure.
 */
export function resolveServiceEndpoint(rawUrl: string | undefined): {
  host: string;
  insecure: boolean;
} {
  const value = (rawUrl || '').trim();
  const schemeMatch = value.match(/^([a-z][a-z0-9+.-]*):\/\//i);
  const scheme = schemeMatch ? schemeMatch[1].toLowerCase() : null;
  const host = value.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '').split('/')[0];

  let insecure: boolean;
  if (scheme === 'http' || scheme === 'ws') {
    insecure = true;
  } else if (scheme === 'https' || scheme === 'wss') {
    insecure = false;
  } else {
    insecure = /^(localhost|127\.|0\.0\.0\.0|\[::1\])/i.test(host);
  }

  return { host, insecure };
}

export const BackendWebSocketService = {
  socket: null as WebSocket | null,
  isConnected: false,
  reconnectAttempts: 0,
  sentMessageIds: new Set<string>(),
  lastProcessedTimestamp: null as string | null,
  processedEventCount: 0,
  pingInterval: null as number | null,

  // Translation callback system for components to subscribe to translation events
  translationCallbacks: new Map<
    string,
    {
      onLoading?: (loading: boolean) => void;
      onResult?: (result: string) => void;
      onError?: (error: string) => void;
    }
  >(),

  // Track pending translation requests to map responses back to requesting components
  pendingTranslationRequests: new Map<string, string>(), // requestId -> componentId

  // Track which component is currently translating so that we can reflect correct translations per section
  currentlyTranslatingComponent: null as string | null,

  /**
   * Connect to backend WebSocket with authentication
   */
  async connect(): Promise<boolean> {
    const authStore = useAuthStore();
    const messageStore = useMessageStore();

    try {
      // Check session status first to understand current state
      const sessionStatus = await this.getSessionStatus(
        authStore.conversationId! // If no conversation id during auth, we will not reach here, so that's why ! is added
      );
      if (sessionStatus) {
        this.processSessionStatus(sessionStatus);
      }

      // Normalize the configured endpoint to a bare host[:port]; a local
      // middleware (e.g. localhost:5000) is served over ws/http, deployed
      // workers over wss/https.
      const { host, insecure } = resolveServiceEndpoint(
        config.aiService.serviceUrl
      );
      const wsScheme = insecure ? 'ws' : 'wss';
      // Encode each dynamic path segment to prevent reserved characters from
      // malforming the WebSocket URL. The worker accepts an optional 4th
      // segment for the agent language: /genesys/{cId}/{lang}[/{agentLang}].
      const convId = encodeURIComponent(authStore.conversationId!);
      const lang = encodeURIComponent(authStore.language!);
      const agentLang = encodeURIComponent(authStore.agentLang);
      const wsUrl = `${wsScheme}://${host}/genesys/${convId}/${lang}/${agentLang}`;
      const tokenProtocol = generateAuthToken(config.genesysCloud.clientId);

      this.socket = new WebSocket(wsUrl, [tokenProtocol]);

      return new Promise((resolve) => {
        if (!this.socket) {
          resolve(false);
          return;
        }

        this.socket.onopen = () => {
          this.isConnected = true;
          this.reconnectAttempts = 0;
          messageStore.setBackendConnectionStatus(true);

          // Start ping mechanism to prevent hibernation (every 3 seconds)
          this.startPingInterval();

          resolve(true);
        };

        this.socket.onmessage = (event) => {
          this.handleBackendMessage(event.data);
        };

        this.socket.onerror = (error) => {
          console.error('Backend WebSocket error:', error);
          this.isConnected = false;
          messageStore.setBackendConnectionStatus(false);
          resolve(false);
        };

        this.socket.onclose = () => {
          this.isConnected = false;
          this.stopPingInterval();
          messageStore.setBackendConnectionStatus(false);
          this.handleReconnection();
        };

        // Timeout after 5 seconds to prevent hanging
        setTimeout(() => {
          if (!this.isConnected) {
            resolve(false);
          }
        }, 5000);
      });
    } catch (error) {
      console.error('Failed to connect to backend WebSocket:', error);
      return false;
    }
  },

  /**
   * Get current session status from backend
   */
  async getSessionStatus(
    conversationId: string
  ): Promise<SessionStatus | null> {
    try {
      const { host, insecure } = resolveServiceEndpoint(
        config.aiService.serviceUrl
      );
      const httpScheme = insecure ? 'http' : 'https';
      const response = await fetch(
        `${httpScheme}://${host}/session/${conversationId}/status`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': config.aiService.apiKey,
          },
        }
      );

      if (response.ok) {
        const status = await response.json();
        return status;
      } else {
        console.warn('Failed to get session status');
        return null;
      }
    } catch (error) {
      console.error('Error getting session status:', error);
      return null;
    }
  },

  /**
   * Process session status from backend to restore application state after page refresh/reconnection.
   * This method synchronizes the frontend with the backend's current session state, ensuring
   * proper UI feedback and preventing duplicate message processing.
   */
  processSessionStatus(status: SessionStatus) {
    const messageStore = useMessageStore();
    // Track how many events the backend has already processed
    // This prevents re-sending historical messages during page refresh or reconnection
    if (status.event_count !== undefined) {
      this.processedEventCount = status.event_count;
    }

    // Update the last processed timestamp to prevent re-sending old messages
    if (status.last_event_time) {
      this.lastProcessedTimestamp = status.last_event_time;
    }

    // Restore alarm system state - critical for showing correct UI after page refresh
    // When alarm is scheduled, it means AI will respond after customer inactivity timeout
    if (status.alarm_scheduled !== undefined) {
      if (status.alarm_scheduled) {
        // Show "waiting for response" indicator when alarm is active
        // This tells the agent that AI will respond soon, preventing interference
        messageStore.setAiResponseProcessingStatus(
          true,
          PROCESSING_STAGE.GENERATING
        );
      }
    }

    // Store the exact time when AI alarm will fire for countdown timers
    // Enables precise "AI responds in 3 seconds..." status messages
    if (status.alarm_time) {
      // Currently not implemented - would be used for countdown UI
    }

    // Check if there are AI responses waiting to be delivered to the conversation
    // This handles cases where responses were generated but not yet sent to Genesys
    if (status.pending_messages !== undefined && status.pending_messages > 0) {
      // Indicate that AI response is ready and waiting for delivery
      messageStore.setAiResponseProcessingStatus(
        true,
        PROCESSING_STAGE.COMPLETED
      );
    }
  },

  /**
   * Send message to backend if not already sent
   */
  async sendMessageToBackend(
    message: Message,
    skipDuplicateCheck = false
  ): Promise<boolean> {
    // Initialize stores once at module level
    const authStore = useAuthStore();

    if (!this.isConnected || !this.socket) {
      console.warn('Backend WebSocket not connected');
      return false;
    }

    // Check for duplicates unless explicitly skipped
    if (!skipDuplicateCheck && this.sentMessageIds.has(message.id)) {
      return true;
    }

    try {
      // Only send inbound and outbound messages, filter out 'unknown'
      const direction =
        message.direction === MESSAGE_DIRECTION.UNKNOWN
          ? MESSAGE_DIRECTION.INBOUND
          : message.direction;

      const cloudEvent: GenesysCloudEvent = {
        specversion: CLOUD_EVENT.SPEC_VERSION,
        type: CLOUD_EVENT.TYPE,
        source: CLOUD_EVENT.SOURCE,
        id: `fe-${crypto.randomUUID()}`,
        time: new Date().toISOString(),
        datacontenttype: CLOUD_EVENT.CONTENT_TYPE,
        data: {
          topicName: GENESYS_CLOUD.TOPIC_NAME,
          eventTime: message.timestamp || new Date().toISOString(),
          eventBody: {
            id: message.id,
            conversationId: authStore.conversationId || '',
            timestamp: message.timestamp || new Date().toISOString(),
            mediaType: 'message',
            body: message.text,
            direction,
            from: {
              id:
                direction === MESSAGE_DIRECTION.INBOUND
                  ? PARTICIPANT_TYPE.CUSTOMER
                  : PARTICIPANT_TYPE.AGENT,
              name:
                direction === MESSAGE_DIRECTION.INBOUND
                  ? PARTICIPANT_DISPLAY_NAME.CUSTOMER
                  : PARTICIPANT_DISPLAY_NAME.AGENT,
            },
            to: {
              id:
                direction === MESSAGE_DIRECTION.INBOUND
                  ? PARTICIPANT_TYPE.AGENT
                  : PARTICIPANT_TYPE.CUSTOMER,
              name:
                direction === MESSAGE_DIRECTION.INBOUND
                  ? PARTICIPANT_DISPLAY_NAME.AGENT
                  : PARTICIPANT_DISPLAY_NAME.CUSTOMER,
            },
            messengerType: 'web',
          },
        },
      };

      this.socket.send(JSON.stringify(cloudEvent));
      this.sentMessageIds.add(message.id);

      // Update the last processed timestamp to track the latest message sent
      if (message.timestamp) {
        this.lastProcessedTimestamp = message.timestamp;
      }

      return true;
    } catch (error) {
      console.error('Failed to send message to backend:', error);
      return false;
    }
  },

  /**
   * Send translation request to backend
   */
  async sendTranslationRequest(
    text: string,
    componentId?: string
  ): Promise<boolean> {
    const authStore = useAuthStore();

    if (!this.isConnected || !this.socket) {
      console.warn('Backend WebSocket not connected');
      return false;
    }

    // Prevent multiple simultaneous translations to avoid global state issues
    if (
      this.currentlyTranslatingComponent &&
      this.currentlyTranslatingComponent !== componentId
    ) {
      console.warn('Another component is already translating');
      if (componentId && this.translationCallbacks.has(componentId)) {
        const callbacks = this.translationCallbacks.get(componentId)!;
        callbacks.onError?.('Another translation is in progress');
      }
      return false;
    }

    try {
      const requestId = `translate-${crypto.randomUUID()}`;
      const cloudEvent = {
        specversion: CLOUD_EVENT.SPEC_VERSION,
        type: CLOUD_EVENT_TYPES.TRANSLATE,
        source: CLOUD_EVENT.SOURCE,
        id: requestId,
        time: new Date().toISOString(),
        datacontenttype: CLOUD_EVENT.CONTENT_TYPE,
        data: {
          text,
          target: authStore.language || 'en-us',
          agent_language: authStore.agentLang,
          componentId, // Track which component made the request
        } as TranslationRequestData & { componentId?: string },
      };

      // Track this request and set as currently translating component
      if (componentId) {
        this.pendingTranslationRequests.set(requestId, componentId);
        this.currentlyTranslatingComponent = componentId;
      }

      this.socket.send(JSON.stringify(cloudEvent));

      // Notify component that translation started
      if (componentId && this.translationCallbacks.has(componentId)) {
        const callbacks = this.translationCallbacks.get(componentId)!;
        callbacks.onLoading?.(true);
      }

      return true;
    } catch (error) {
      console.error('Failed to send translation request:', error);

      // Notify component of error
      if (componentId && this.translationCallbacks.has(componentId)) {
        const callbacks = this.translationCallbacks.get(componentId)!;
        callbacks.onError?.('Translation request failed');
      }

      return false;
    }
  },

  /**
   * Send summary request to backend
   */
  async sendSummaryRequest(): Promise<boolean> {
    if (!this.isConnected || !this.socket) {
      console.warn('Backend WebSocket not connected');
      return false;
    }

    try {
      const authStore = useAuthStore();
      const requestId = `summary-${crypto.randomUUID()}`;
      const cloudEvent = {
        specversion: CLOUD_EVENT.SPEC_VERSION,
        type: CLOUD_EVENT_TYPES.SUMMARY,
        source: CLOUD_EVENT.SOURCE,
        id: requestId,
        time: new Date().toISOString(),
        datacontenttype: CLOUD_EVENT.CONTENT_TYPE,
        data: {
          agent_language: authStore.agentLang,
        },
      };

      this.socket.send(JSON.stringify(cloudEvent));
      return true;
    } catch (error) {
      console.error('Failed to send summary request:', error);
      return false;
    }
  },

  /**
   * Request a summary of the current (prior) segment WITHOUT clearing events.
   * Used by the resume flow; the worker flags the response as `previousSegment`
   * so it routes to the resume prompt instead of the manual summary panel.
   */
  async sendSegmentSummary(): Promise<boolean> {
    if (!this.isConnected || !this.socket) {
      console.warn('Backend WebSocket not connected');
      return false;
    }

    try {
      const authStore = useAuthStore();
      const cloudEvent = {
        specversion: CLOUD_EVENT.SPEC_VERSION,
        type: CLOUD_EVENT_TYPES.SEGMENT_SUMMARY,
        source: CLOUD_EVENT.SOURCE,
        id: `segment-summary-${crypto.randomUUID()}`,
        time: new Date().toISOString(),
        datacontenttype: CLOUD_EVENT.CONTENT_TYPE,
        data: {
          agent_language: authStore.agentLang,
        },
      };

      this.socket.send(JSON.stringify(cloudEvent));
      return true;
    } catch (error) {
      console.error('Failed to send segment summary request:', error);
      return false;
    }
  },

  /**
   * Start a new segment: clear the worker's accumulated events (and alarm).
   * When `keepSummaryAsContext` is true the prior-segment summary is retained
   * as context for the next AI response.
   */
  async sendSegmentReset(opts: {
    keepSummaryAsContext: boolean;
    boundaryId: string;
  }): Promise<boolean> {
    if (!this.isConnected || !this.socket) {
      console.warn('Backend WebSocket not connected');
      return false;
    }

    try {
      const cloudEvent = {
        specversion: CLOUD_EVENT.SPEC_VERSION,
        type: CLOUD_EVENT_TYPES.SEGMENT_RESET,
        source: CLOUD_EVENT.SOURCE,
        id: `segment-reset-${crypto.randomUUID()}`,
        time: new Date().toISOString(),
        datacontenttype: CLOUD_EVENT.CONTENT_TYPE,
        data: {
          keepSummaryAsContext: opts.keepSummaryAsContext,
          boundaryId: opts.boundaryId,
        },
      };

      this.socket.send(JSON.stringify(cloudEvent));
      return true;
    } catch (error) {
      console.error('Failed to send segment reset request:', error);
      return false;
    }
  },

  /**
   * Continue the conversation with the full accumulated context (no reset).
   */
  async sendSegmentContinue(boundaryId: string): Promise<boolean> {
    if (!this.isConnected || !this.socket) {
      console.warn('Backend WebSocket not connected');
      return false;
    }

    try {
      const cloudEvent = {
        specversion: CLOUD_EVENT.SPEC_VERSION,
        type: CLOUD_EVENT_TYPES.SEGMENT_CONTINUE,
        source: CLOUD_EVENT.SOURCE,
        id: `segment-continue-${crypto.randomUUID()}`,
        time: new Date().toISOString(),
        datacontenttype: CLOUD_EVENT.CONTENT_TYPE,
        data: { boundaryId },
      };

      this.socket.send(JSON.stringify(cloudEvent));
      return true;
    } catch (error) {
      console.error('Failed to send segment continue request:', error);
      return false;
    }
  },

  /**
   * Forward the conversation's dealer number to the worker so summaries and AI
   * responses can reference it. Persisted server-side; only needs sending once.
   */
  async sendDealerInfo(dealerNumber: string): Promise<boolean> {
    if (!this.isConnected || !this.socket) {
      console.warn('Backend WebSocket not connected');
      return false;
    }

    try {
      const cloudEvent = {
        specversion: CLOUD_EVENT.SPEC_VERSION,
        type: CLOUD_EVENT_TYPES.DEALER_INFO,
        source: CLOUD_EVENT.SOURCE,
        id: `dealer-info-${crypto.randomUUID()}`,
        time: new Date().toISOString(),
        datacontenttype: CLOUD_EVENT.CONTENT_TYPE,
        data: { dealerNumber },
      };

      this.socket.send(JSON.stringify(cloudEvent));
      return true;
    } catch (error) {
      console.error('Failed to send dealer info:', error);
      return false;
    }
  },

  /**
   * Send similarity request to backend for monitoring purposes
   * This is called when agents send messages to customers to compare
   * the AI-generated response with what the agent actually sent
   */
  async sendSimilarityRequest(text: string, text_2: string): Promise<boolean> {
    if (!this.isConnected || !this.socket) {
      console.warn('Backend WebSocket not connected');
      return false;
    }

    try {
      const requestId = `similarity-${crypto.randomUUID()}`;
      const cloudEvent = {
        specversion: CLOUD_EVENT.SPEC_VERSION,
        type: CLOUD_EVENT_TYPES.SIMILARITY,
        source: CLOUD_EVENT.SOURCE,
        id: requestId,
        time: new Date().toISOString(),
        datacontenttype: CLOUD_EVENT.CONTENT_TYPE,
        data: {
          text,
          text_2,
        },
      };

      this.socket.send(JSON.stringify(cloudEvent));
      return true;
    } catch (error) {
      console.error('Failed to send similarity request:', error);
      return false;
    }
  },

  /**
   * Handle incoming messages from backend
   */
  handleBackendMessage(data: string) {
    try {
      const response = JSON.parse(data);

      // Handle CloudEvent responses from backend
      if (response.specversion && response.type && response.data) {
        // This is a CloudEvent response
        this.handleCloudEventResponse(response);
        return;
      }

      // Handle different response types
      if (response.data) {
        this.processBackendResponse(response.data);
      } else if (response.session_restored) {
        // Session restored successfully
        return;
      } else {
        // Handle raw response
        this.processBackendResponse(response);
      }
    } catch {
      // Non-JSON frames (e.g. backend keepalive/acknowledgement strings such as
      // "Event processed successfully") are not actionable payloads. Ignore them
      // quietly instead of erroring or mis-treating them as a translation.
      console.debug('Ignoring non-JSON backend message:', data);
    }
  },

  /**
   * Handle connection established event - restores session state after reconnection
   */
  handleConnectionEstablished(eventData: ConnectionEventData) {
    const messageStore = useMessageStore();

    if (!eventData?.session_restored) return;

    // Check alarm system state
    if (eventData.alarm_scheduled) {
      messageStore.setAiResponseProcessingStatus(
        true,
        PROCESSING_STAGE.GENERATING
      );
    }

    // Check for pending messages that are ready for delivery
    if (
      eventData.has_pending_messages ||
      (eventData.pending_messages ?? 0) > 0
    ) {
      messageStore.setAiResponseProcessingStatus(
        true,
        PROCESSING_STAGE.COMPLETED
      );
    }

    // Update event count to prevent duplicate processing
    if (
      eventData.event_count &&
      eventData.event_count > this.processedEventCount
    ) {
      this.processedEventCount = eventData.event_count;
    }

    // Restore resumable-conversation segment state. The worker is the source of
    // truth: an iframe reload wipes the store but the Durable Object re-supplies
    // the acknowledged boundary so we do not re-prompt for a resolved segment.
    if (eventData.segment_mode) {
      messageStore.setSegmentMode(eventData.segment_mode);
    }
    if (eventData.acked_boundary_id !== undefined) {
      messageStore.setAckedBoundaryId(eventData.acked_boundary_id ?? '');
    }
    // Now that the acknowledged boundary is known, decide whether a boundary
    // detected during history load still needs the resume prompt.
    this.maybePromptResume();
  },

  /**
   * Record a verbose middleware activity entry for the processing feed.
   * Substitutes {attempt}/{max} placeholders when provided (retry copy).
   */
  recordActivity(phase: string, opts?: { attempt?: number; max?: number }) {
    const messageStore = useMessageStore();
    const params =
      opts?.attempt != null && opts?.max != null
        ? { attempt: opts.attempt, max: opts.max }
        : {};
    const text = i18n.global.t(`middleware.${phase}`, params);
    messageStore.pushActivity(phase, text);
  },

  /**
   * Evaluate whether a detected segment boundary requires the resume prompt.
   * Only fires while connected and when the detected boundary differs from the
   * acknowledged one, guaranteeing idempotency across reloads.
   */
  maybePromptResume() {
    if (!this.isConnected) return;

    const messageStore = useMessageStore();
    const detected = messageStore.detectedBoundaryId;

    if (!detected) return;
    if (detected === messageStore.ackedBoundaryId) return;
    if (messageStore.pendingResumeDecision) return;

    messageStore.openResumeDecision(detected);
    messageStore.setPreviousSegmentSummaryGenerating(true);
    this.recordActivity(MIDDLEWARE_PHASE.RESUMED);
    void this.sendSegmentSummary();
  },

  /**
   * Handle a verbose middleware status event (phase transitions).
   */
  handleStatus(eventData: StatusEventData) {
    if (!eventData?.phase) return;
    this.recordActivity(eventData.phase);
  },

  /**
   * Handle a workflow retry event - surface the retry attempt in the UI.
   */
  handleWorkflowRetry(eventData: WorkflowRetryData, subject?: string) {
    const messageStore = useMessageStore();
    const messageId = this.extractMessageIdFromSubject(subject);
    const attempt = eventData?.attempt ?? 0;
    const max = eventData?.max_attempts ?? 0;

    if (messageId) {
      messageStore.setMessageProcessingStatus(
        messageId,
        true,
        PROCESSING_STAGE.RETRYING,
        attempt
      );
    } else {
      messageStore.setAiResponseProcessingStatus(
        true,
        PROCESSING_STAGE.RETRYING
      );
    }
    messageStore.aiResponseProcessing.attempt = attempt;
    messageStore.aiResponseProcessing.maxAttempts = max;
    this.recordActivity(MIDDLEWARE_PHASE.RETRYING, { attempt, max });
  },

  /**
   * Handle a workflow failed event - the worker exhausted all retries.
   */
  handleWorkflowFailed(eventData: WorkflowFailedData, subject?: string) {
    const messageStore = useMessageStore();
    const messageId = this.extractMessageIdFromSubject(subject);
    const errorMessage = eventData?.error || 'Workflow failed';

    if (messageId) {
      messageStore.setMessageAiError(messageId, errorMessage);
    } else {
      messageStore.setAiResponseError(errorMessage);
    }
    this.recordActivity(MIDDLEWARE_PHASE.FAILED);
  },

  /**
   * Extract message ID from CloudEvent subject field
   * Subject format: "answer-{trigger_message_id}" or similar patterns
   */
  extractMessageIdFromSubject(subject?: string): string | null {
    if (!subject) return null;

    // Pattern: "answer-{message_id}", "response-{message_id}", etc.
    const match = subject.match(/^(?:answer|response|message)-(.+)$/);
    return match ? match[1] : null;
  },

  /**
   * Handle generating response event - AI processing started
   */
  handleGeneratingResponse(eventData: ProcessingEventData, subject?: string) {
    const messageStore = useMessageStore();

    // Extract message ID from subject
    const messageId = this.extractMessageIdFromSubject(subject);

    if (eventData?.event_count) {
      this.processedEventCount = eventData.event_count;
    }

    this.recordActivity(MIDDLEWARE_PHASE.GENERATING);

    if (messageId) {
      // Set processing state for specific message
      messageStore.setMessageProcessingStatus(
        messageId,
        true,
        PROCESSING_STAGE.GENERATING
      );
      console.warn(
        `AI processing started for message: ${messageId}`,
        eventData
      );
    } else {
      // Fallback to legacy global processing state
      messageStore.setAiResponseProcessingStatus(
        true,
        PROCESSING_STAGE.GENERATING
      );
      console.warn(
        'No message ID in subject, using legacy processing state',
        subject
      );
    }
  },

  /**
   * Handle AI response event - process the actual translation
   */
  handleAIResponse(eventData: unknown, subject?: string) {
    const messageId = this.extractMessageIdFromSubject(subject);

    if (messageId) {
      console.warn(`AI response received for message: ${messageId}`, eventData);
    }

    this.recordActivity(MIDDLEWARE_PHASE.RESPONDED);

    // Process response with message ID context
    this.processBackendResponse(eventData as BackendResponse, messageId);
  },

  /**
   * Handle status update event - legacy status handling
   */
  handleStatusUpdate(eventData: StatusUpdateEventData, subject?: string) {
    const messageStore = useMessageStore();
    const messageId = this.extractMessageIdFromSubject(subject);

    if (eventData?.status === PROCESSING_STAGE.GENERATING) {
      if (messageId) {
        messageStore.setMessageProcessingStatus(
          messageId,
          true,
          PROCESSING_STAGE.GENERATING
        );
      } else {
        messageStore.setAiResponseProcessingStatus(
          true,
          PROCESSING_STAGE.GENERATING
        );
      }
    } else if (eventData?.status === PROCESSING_STAGE.COMPLETED) {
      if (messageId) {
        messageStore.setMessageProcessingStatus(
          messageId,
          false,
          PROCESSING_STAGE.COMPLETED
        );
      } else {
        messageStore.setAiResponseProcessingStatus(
          false,
          PROCESSING_STAGE.COMPLETED
        );
      }
    } else if (eventData?.status === PROCESSING_STAGE.SENDING) {
      if (messageId) {
        messageStore.setMessageProcessingStatus(
          messageId,
          true,
          PROCESSING_STAGE.SENDING
        );
      } else {
        messageStore.setAiResponseProcessingStatus(
          true,
          PROCESSING_STAGE.SENDING
        );
      }
    } else if (eventData?.status === PROCESSING_STAGE.ERROR) {
      if (messageId) {
        messageStore.setMessageProcessingStatus(
          messageId,
          false,
          PROCESSING_STAGE.ERROR
        );
      } else {
        messageStore.setAiResponseProcessingStatus(
          false,
          PROCESSING_STAGE.ERROR
        );
      }
    } else {
      console.warn(
        'Unexpected status in handleStatusUpdate:',
        eventData?.status
      );
    }
  },

  /**
   * Handle error event - process backend errors
   */
  handleErrorEvent(eventData: ErrorEventData, subject?: string) {
    const messageStore = useMessageStore();
    const errorMessage = eventData?.error_message || 'Unknown error';

    // Extract message ID from subject
    const messageId = this.extractMessageIdFromSubject(subject);

    if (messageId) {
      // Set error for specific message
      messageStore.setMessageAiError(messageId, errorMessage);
      console.error(`Error for message ${messageId}:`, errorMessage);
    } else {
      // Fallback to legacy global error
      messageStore.setAiResponseError(errorMessage);
      console.error('Error:', errorMessage);
    }
  } /**
   * Handle generating translation event - translation processing started
   */,
  handleGeneratingTranslation(_eventData: TranslationStatusData) {
    console.warn('Translation generation started');

    // Only notify the component that is currently translating
    if (
      this.currentlyTranslatingComponent &&
      this.translationCallbacks.has(this.currentlyTranslatingComponent)
    ) {
      const callbacks = this.translationCallbacks.get(
        this.currentlyTranslatingComponent
      )!;
      callbacks.onLoading?.(true);
    } else {
      // Fallback: notify all registered components that translation is loading
      this.translationCallbacks.forEach((callbacks) => {
        callbacks.onLoading?.(true);
      });
    }
  },

  /**
   * Handle translation response event - process the actual translation result
   */
  handleTranslationResponse(eventData: TranslationResponseData) {
    // Extract the translation result from the response data
    const translationResult =
      typeof eventData === 'string' ? eventData : eventData?.data;

    if (
      translationResult &&
      typeof translationResult === 'string' &&
      translationResult.trim()
    ) {
      // Only notify the component that is currently translating
      if (
        this.currentlyTranslatingComponent &&
        this.translationCallbacks.has(this.currentlyTranslatingComponent)
      ) {
        const callbacks = this.translationCallbacks.get(
          this.currentlyTranslatingComponent
        )!;
        callbacks.onResult?.(translationResult);
        callbacks.onLoading?.(false);
      } else {
        // Fallback: notify all components if we can't determine the specific one
        this.translationCallbacks.forEach((callbacks) => {
          callbacks.onResult?.(translationResult);
          callbacks.onLoading?.(false);
        });
      }

      // Clear the currently translating component
      this.currentlyTranslatingComponent = null;
    } else {
      // Handle invalid response
      if (
        this.currentlyTranslatingComponent &&
        this.translationCallbacks.has(this.currentlyTranslatingComponent)
      ) {
        const callbacks = this.translationCallbacks.get(
          this.currentlyTranslatingComponent
        )!;
        callbacks.onError?.('Invalid translation response');
        callbacks.onLoading?.(false);
      } else {
        // Fallback: notify all components
        this.translationCallbacks.forEach((callbacks) => {
          callbacks.onError?.('Invalid translation response');
          callbacks.onLoading?.(false);
        });
      }

      // Clear the currently translating component
      this.currentlyTranslatingComponent = null;
    }
  },

  /**
   * Handle generating summary event - summary processing started
   */
  handleGeneratingSummary(eventData: SummaryStatusData) {
    const messageStore = useMessageStore();
    this.recordActivity(MIDDLEWARE_PHASE.SUMMARIZING);
    if (eventData?.previousSegment) {
      messageStore.setPreviousSegmentSummaryGenerating(true);
      return;
    }
    messageStore.setSummaryGenerating(true);
  },

  /**
   * Handle summary response event - process the actual summary result
   */
  handleSummaryResponse(eventData: SummaryResponseData) {
    const messageStore = useMessageStore();
    this.recordActivity(MIDDLEWARE_PHASE.SUMMARIZED);
    if (eventData?.previousSegment) {
      if (eventData.summary) {
        messageStore.setPreviousSegmentSummary(eventData.summary);
      } else {
        messageStore.setPreviousSegmentSummaryGenerating(false);
      }
      return;
    }
    if (eventData.summary) {
      messageStore.setSummaryResponse(eventData.summary);
    } else {
      messageStore.setSummaryError('Empty summary received');
    }
  },

  /**
   * Handle summary error event - process summary errors
   */
  handleSummaryError(eventData: SummaryErrorData) {
    const messageStore = useMessageStore();
    if (eventData?.previousSegment) {
      messageStore.setPreviousSegmentSummaryGenerating(false);
      console.error('Segment summary error:', eventData.error);
      return;
    }
    messageStore.setSummaryError(eventData.error || 'Unknown summary error');
    console.error('Summary error:', eventData.error);
  },

  /**
   * Handle generating similarity event - similarity processing started
   */
  handleGeneratingSimilarity(_eventData: SimilarityStatusData) {
    // No UI updates needed - this is for monitoring only
    // The backend has started processing the similarity request
  },

  /**
   * Handle similarity response event - process the similarity score
   */
  handleSimilarityResponse(eventData: SimilarityResponseData) {
    // No UI updates needed - this is for monitoring only
    // The similarity score can be viewed in the network tab for monitoring purposes
    console.warn('Similarity score received:', eventData.similarity_score);
  },

  /**
   * Handle similarity error event - process similarity errors
   */
  handleSimilarityError(eventData: SimilarityErrorData) {
    // No UI updates needed - this is for monitoring only
    console.error('Similarity error:', eventData.error);
  },

  /**
   * Handle CloudEvent responses from backend
   */
  handleCloudEventResponse(cloudEvent: BackendCloudEvent) {
    console.warn(
      'BackendWebSocketService: Received CloudEvent:',
      cloudEvent.type,
      cloudEvent
    );

    // Check the CloudEvent type to understand what kind of response this is
    switch (cloudEvent.type) {
      case CLOUD_EVENT_TYPES.CONNECTION_ESTABLISHED:
        this.handleConnectionEstablished(
          cloudEvent.data as ConnectionEventData
        );
        break;

      case CLOUD_EVENT_TYPES.GENERATING_RESPONSE:
        this.handleGeneratingResponse(
          cloudEvent.data as ProcessingEventData,
          cloudEvent.subject
        );
        break;

      case CLOUD_EVENT_TYPES.RESPONSE:
        this.handleAIResponse(cloudEvent.data, cloudEvent.subject);
        break;

      case CLOUD_EVENT_TYPES.STATUS_UPDATE:
        this.handleStatusUpdate(
          cloudEvent.data as StatusUpdateEventData,
          cloudEvent.subject
        );
        break;

      case CLOUD_EVENT_TYPES.STATUS:
        this.handleStatus(cloudEvent.data as StatusEventData);
        break;

      case CLOUD_EVENT_TYPES.WORKFLOW_RETRY:
        this.handleWorkflowRetry(
          cloudEvent.data as WorkflowRetryData,
          cloudEvent.subject
        );
        break;

      case CLOUD_EVENT_TYPES.WORKFLOW_FAILED:
        this.handleWorkflowFailed(
          cloudEvent.data as WorkflowFailedData,
          cloudEvent.subject
        );
        break;

      case CLOUD_EVENT_TYPES.SEGMENT_RESET_ACK:
      case CLOUD_EVENT_TYPES.SEGMENT_CONTINUE_ACK:
        // The agent's choice is applied optimistically in the store when the
        // resume prompt is resolved; the ack merely confirms the worker state.
        return;

      case CLOUD_EVENT_TYPES.DEALER_INFO_ACK:
        // The worker acknowledges it stored the dealer number; nothing to do.
        return;

      case CLOUD_EVENT_TYPES.ERROR:
        this.handleErrorEvent(
          cloudEvent.data as ErrorEventData,
          cloudEvent.subject
        );
        break;

      case CLOUD_EVENT_TYPES.EVENT_PROCESSED:
        // Don't update any UI state for processed events
        return;

      case CLOUD_EVENT_TYPES.GENERATING_TRANSLATION:
        this.handleGeneratingTranslation(
          cloudEvent.data as TranslationStatusData
        );
        break;

      case CLOUD_EVENT_TYPES.TRANSLATE_RESPONSE:
        this.handleTranslationResponse(
          cloudEvent.data as TranslationResponseData
        );
        break;

      case CLOUD_EVENT_TYPES.GENERATING_SUMMARY:
        this.handleGeneratingSummary(cloudEvent.data as SummaryStatusData);
        break;

      case CLOUD_EVENT_TYPES.SUMMARY_RESPONSE:
        this.handleSummaryResponse(cloudEvent.data as SummaryResponseData);
        break;

      case CLOUD_EVENT_TYPES.SUMMARY_ERROR:
        this.handleSummaryError(cloudEvent.data as SummaryErrorData);
        break;

      case CLOUD_EVENT_TYPES.GENERATING_SIMILARITY:
        this.handleGeneratingSimilarity(
          cloudEvent.data as SimilarityStatusData
        );
        break;

      case CLOUD_EVENT_TYPES.SIMILARITY_RESPONSE:
        this.handleSimilarityResponse(
          cloudEvent.data as SimilarityResponseData
        );
        break;

      case CLOUD_EVENT_TYPES.SIMILARITY_ERROR:
        this.handleSimilarityError(cloudEvent.data as SimilarityErrorData);
        break;

      default:
        console.warn('Unknown CloudEvent type:', cloudEvent.type);
        // Fallback: process any data in the CloudEvent
        if (cloudEvent.data) {
          const messageId = this.extractMessageIdFromSubject(
            cloudEvent.subject
          );
          this.processBackendResponse(
            cloudEvent.data as BackendResponse,
            messageId
          );
        }
    }
  },

  /**
   * Process backend AI response
   */
  processBackendResponse(
    response: BackendResponse,
    messageId?: string | null
  ): void {
    const messageStore = useMessageStore();

    if (response.error) {
      console.error('Error:', response.error);
      if (messageId) {
        messageStore.setMessageAiError(messageId, response.error);
      } else {
        messageStore.setAiResponseError(response.error);
      }
      return;
    }

    // Transform response if it has format/response structure
    const transformedResponse = transformFormattedResponse(response);

    // Handle response with answer/answer_agent_lang (preferred) or legacy answer_agent/answer_nl fallback
    if (
      transformedResponse?.answer ||
      transformedResponse?.answer_agent_lang ||
      transformedResponse?.answer_agent ||
      transformedResponse?.answer_nl
    ) {
      const additionalInfo = buildAdditionalInfo(transformedResponse);

      const aiResponse = {
        translation: transformedResponse.answer, // Response in the customer's language
        agentAnswer:
          transformedResponse.answer_agent_lang ??
          transformedResponse.answer_agent ??
          transformedResponse.answer_nl,
        originalMessage:
          transformedResponse.question_agent_lang ??
          transformedResponse.question_agent ??
          transformedResponse.question_nl ??
          transformedResponse.question,
        additionalInfo,
        timestamp: new Date().toISOString(),
      };

      if (messageId) {
        // Set response for specific message
        messageStore.setMessageAiResponse(messageId, aiResponse);
        messageStore.setMessageProcessingStatus(messageId, false);
      } else {
        // Fallback to legacy global response
        messageStore.setLatestAiResponse(aiResponse);
        messageStore.clearAiResponseProcessing();
      }
      return;
    }
  },

  /**
   * Send new messages that haven't been processed yet
   *
   * FLOW EXPLANATION:
   * 1. Filter messages based on duplicate/timestamp checks
   * 2. For resumed sessions (processedEventCount > 0) we rely on lastProcessedTimestamp
   *    to skip already-processed messages. Without a timestamp boundary we can't
   *    safely determine which messages are new, so we skip entirely.
   * 3. Send all remaining messages sequentially to maintain conversation order
   */
  async sendNewMessages(messages: Message[]) {
    // If the backend has prior events but no timestamp boundary we cannot
    // distinguish new-segment messages from already-processed ones → skip.
    if (this.processedEventCount > 0 && !this.lastProcessedTimestamp) {
      return;
    }

    // Filter messages that need to be sent
    const messagesToSend: Message[] = [];

    for (const message of messages) {
      // Skip if already sent
      if (this.sentMessageIds.has(message.id)) {
        continue;
      }

      // Skip messages the backend has already processed (prior segment)
      if (
        this.lastProcessedTimestamp &&
        message.timestamp <= this.lastProcessedTimestamp
      ) {
        continue;
      }

      messagesToSend.push(message);
    }

    if (messagesToSend.length === 0) {
      return;
    }

    // Send messages sequentially to maintain order
    for (const message of messagesToSend) {
      await this.sendMessageToBackend(message);
      // Small delay to prevent overwhelming the backend
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  },

  /**
   * Handle reconnection logic
   */
  handleReconnection() {
    if (this.reconnectAttempts < config.webSocket.maxReconnectAttempts) {
      this.reconnectAttempts++;

      setTimeout(() => {
        this.connect();
      }, config.webSocket.reconnectDelay);
    } else {
      console.error('Max reconnection attempts reached for backend WebSocket');
    }
  },

  /**
   * Disconnect from backend WebSocket
   */
  disconnect() {
    const messageStore = useMessageStore();

    this.stopPingInterval();

    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.isConnected = false;
    this.reconnectAttempts = 0;

    // Reset tracking state on disconnect
    this.sentMessageIds.clear();
    this.lastProcessedTimestamp = null;
    this.processedEventCount = 0;
    this.pendingTranslationRequests.clear();
    this.currentlyTranslatingComponent = null;

    messageStore.setBackendConnectionStatus(false);
  },

  /**
   * Start ping interval to prevent WebSocket hibernation
   * Backend requires pings every 3 seconds to stay active
   */
  startPingInterval() {
    // Clear any existing interval
    this.stopPingInterval();

    this.pingInterval = setInterval(() => {
      if (this.socket && this.isConnected) {
        const pingMessage = {
          type: WS_MESSAGE_TYPE.PING,
          timestamp: new Date().toISOString(),
        };
        try {
          this.socket.send(JSON.stringify(pingMessage));
        } catch (error) {
          console.error('Failed to send ping:', error);
          this.stopPingInterval();
        }
      } else {
        this.stopPingInterval();
      }
    }, 3000) as unknown as number; // Every 3 seconds as required by backend
  },

  /**
   * Stop ping interval
   */
  stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  },

  /**
   * Register a component for translation callbacks
   */
  registerTranslationCallback(
    componentId: string,
    callbacks: {
      onLoading?: (loading: boolean) => void;
      onResult?: (result: string) => void;
      onError?: (error: string) => void;
    }
  ) {
    this.translationCallbacks.set(componentId, callbacks);
  },

  /**
   * Unregister a component from translation callbacks
   */
  unregisterTranslationCallback(componentId: string) {
    this.translationCallbacks.delete(componentId);
  },
};
